import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Modal, Button, Col, Divider, Image, Row, Skeleton, Spin } from "antd";
import moment from "moment";

import Swal from "sweetalert2";

import reviewApi from "../../../apis/Review.api";

// 리뷰 상세보기 팝업 모달
const ReviewDetailModal = ({
  reviewDetailModalShow,
  setReviewDetailModalShow,
  currentReviewId,
  searchOptions,
  orderType,
  orderBy,
  getReadReviews,
}) => {
  const user = useSelector((state) => state.auth.user);

  const [isLoading, setIsLoading] = useState(true);

  const [currReview, setCurrReview] = useState({});
  const [prevReview, setPrevReview] = useState({});
  const [nextReview, setNextReview] = useState({});

  const [isPhotoReview, setIsPhotoReview] = useState(false);
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);

  useEffect(() => {
    getOneReview(currentReviewId, null);
  }, []);

  /**
   * 리뷰 id로 해당 리뷰 상세 가져오기
   * @param id
   * @returns {Promise<void>}
   */
  const getOneReview = async (id, moveTo) => {
    try {
      let param = {
        reviewId: id,
        move: moveTo ? moveTo : null,
        startDate: searchOptions.startDate.format("YYYYMMDDHHmm"),
        endDate: searchOptions.endDate.format("YYYYMMDDHHmm"),
        reviewStatus: searchOptions.reviewStatus,
        reviewGrade: searchOptions.reviewGrade,
        benefitPayment: searchOptions.benefitPayment,
        exposureStatus: searchOptions.exposureStatus,
        bestReview: searchOptions.bestReview,
        searchType: searchOptions.searchType,
        searchText: searchOptions.searchText,
        orderType: orderType,
        orderBy: orderBy,
        userId: user.id === 724 ? 1 : user.id,
      };
      const res = await reviewApi.getReviewDetail(param);
      if (res.code === 200) {
        setCurrReview(res.result.currReview);
        setPrevReview(res.result.prevReview);
        setNextReview(res.result.nextReview);
        setIsPhotoReview(res.result.currReview.review_img_quantity !== 0);
        setCurrentPhotoIdx(0);
        setIsLoading(false);
      }
    } catch (error) {
      Swal.fire({
        title:
          "리뷰 정보를 가져오지 못했습니다.\n새로고침 후 다시 시도해주세요.",
        customClass: "custom-alert",
        confirmButtonColor: "#117ffa",
        confirmButtonText: "확인",
      }).then(() => {
        setReviewDetailModalShow(false);
        setIsLoading(false);
        console.log(error);
      });
    }
  };

  /**
   * 리뷰 이미지가 1개 이상인 경우 이미지 목록 render
   * @returns {*[]}
   */
  const renderReviewImageList = () => {
    let imageList = [];

    for (let i = 0; i < currReview.review_img_quantity; i++) {
      let source = `${process.env.REACT_APP_S3_HOST}/image/review/${
        currReview.product_id
      }/${currReview.created_by}_${currReview.id}_${i}.${
        currReview.review_title.split("-")[1]
      }`;
      imageList.push({ idx: i, source });
    }

    return imageList;
  };

  /**
   * 리뷰 이미지 목록에 있는 이미지 클릭 시 실행
   * @param idx
   */
  const handleReviewImageListClick = (idx) => {
    setCurrentPhotoIdx(idx);
  };

  /**
   * 리뷰 평점 별 render
   * @param value
   * @returns {JSX.Element}
   */
  const renderReviewStar = (value) => {
    let scoreList = [];
    for (let i = 0; i < value; i++) {
      scoreList.push(
        <img
          key={`empty_star_${currReview.id}_${i + 1}`}
          className="review-score-img"
          src={`${process.env.REACT_APP_HOST}/assets/images/inquiry/selectGrade.png`}
        />,
      );
    }
    if (value !== 5) {
      for (let i = 0; i < 5 - value; i++) {
        scoreList.push(
          <img
            key={`star_${currReview.id}_${i + 1}`}
            className="review-score-img"
            src={`${process.env.REACT_APP_HOST}/assets/images/inquiry/unSelectGrade.png`}
          />,
        );
      }
    }
    return (
      <Row align="middle" wrap={false} style={{ padding: "0" }}>
        {scoreList.map((score) => score)}
        &nbsp;
        {value}
      </Row>
    );
  };

  /**
   * 리뷰 베스트 선정/해제 버튼 클릭 시 실행
   * @returns {Promise<void>}
   */
  const handleReviewBest = async () => {
    const isBestResult = currReview.is_best;
    Swal.fire({
      title: isBestResult
        ? "선택된 베스트리뷰를 해제하시겠습니까?\n(상품페이지 리뷰 상단에서 노출되지 않게 됩니다.)"
        : "베스트리뷰 선정 시 사진이 첨부되었거나 꼼꼼하게 작성된 리뷰를 선정해주세요.\n(선정된 베스트리뷰는 상품페이지 리뷰 상단에 자동으로 노출됩니다.)",
      customClass: "custom-alert",
      confirmButtonColor: isBestResult ? "#ff4d4f" : "#117ffa",
      confirmButtonText: "확인",
      showCancelButton: true,
      cancelButtonText: "취소",
    }).then(async ({ isConfirmed }) => {
      if (isConfirmed) {
        setIsLoading(true);
        const param = {
          user_id: user.id === 724 ? 1 : user.id,
          editList: [currReview.id],
          editKey: "is_best",
          is_best: !isBestResult,
        };
        const res = await reviewApi.editReview(param);
        if (res.code === 200) {
          Swal.fire({
            title: "정상적으로 수정이 완료되었습니다.",
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
          await getOneReview(currReview.id, null);
          getReadReviews();
          setIsLoading(false);
        } else if (res.code === 999) {
          setIsLoading(false);
          Swal.fire({
            title: res.msg,
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
        } else {
          setIsLoading(false);
          Swal.fire({
            title:
              "해당 내용을 수정하는데 실패했습니다.\n관리자에게 문의하시길 바랍니다.",
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
        }
      }
    });
  };

  /**
   * 리뷰 노출
   * @returns {Promise<void>}
   */
  const handleReviewShow = async () => {
    Swal.fire({
      title: "노출 처리하시겠습니까?",
      customClass: "custom-alert",
      confirmButtonColor: "#117ffa",
      confirmButtonText: "확인",
      showCancelButton: true,
      cancelButtonText: "취소",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        const param = {
          user_id: user.id === 724 ? 1 : user.id,
          editList: [currReview.id],
          editKey: "review_private",
          editValue: 0,
        };
        const res = await reviewApi.editReview(param);
        if (res.code === 200) {
          Swal.fire({
            title: "정상적으로 수정이 완료 되었습니다.",
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
          await getOneReview(currReview.id, null);
          getReadReviews();
        } else {
          Swal.fire({
            title: res?.msg,
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
        }
      }
      setIsLoading(false);
    });
  };

  /**
   * 리뷰 미노출
   * @returns {Promise<void>}
   */
  const handleReviewNoShow = async () => {
    Swal.fire({
      title: "미노출 처리하시겠습니까?",
      customClass: "custom-alert",
      confirmButtonColor: "#117ffa",
      confirmButtonText: "확인",
      showCancelButton: true,
      cancelButtonText: "취소",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        const param = {
          user_id: user.id === 724 ? 1 : user.id,
          editList: [currReview.id],
          editKey: "review_private",
          editValue: 1,
        };
        const res = await reviewApi.editReview(param);
        if (res.code === 200) {
          Swal.fire({
            title: "정상적으로 수정이 완료 되었습니다.",
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
          await getOneReview(currReview.id, null);
          getReadReviews();
        } else {
          Swal.fire({
            title: res?.msg,
            customClass: "custom-alert",
            confirmButtonColor: "#117ffa",
            confirmButtonText: "확인",
          });
        }
      }
      setIsLoading(false);
    });
  };

  /**
   * 이전 리뷰 클릭 시 실행
   */
  const handlePrevReviewClick = async () => {
    if (prevReview) {
      setIsLoading(true);
      await getOneReview(currReview.id, "prev");
    } else {
      Swal.fire({
        title: "첫번째 리뷰입니다.",
        customClass: "custom-alert",
        confirmButtonColor: "#117ffa",
        confirmButtonText: "확인",
      });
    }
  };

  /**
   * 다음 리뷰 클릭 시 실행
   */
  const handleNextReviewClick = async () => {
    if (nextReview) {
      setIsLoading(true);
      await getOneReview(currReview.id, "next");
    } else {
      Swal.fire({
        title: " 마지막 리뷰입니다.",
        customClass: "custom-alert",
        confirmButtonColor: "#117ffa",
        confirmButtonText: "확인",
      });
    }
  };

  return (
    <Modal
      wrapClassName="review-detail-modal-wrap"
      title="리뷰 상세보기"
      visible={reviewDetailModalShow}
      centered={true}
      onCancel={() => {
        setCurrReview({});
        setReviewDetailModalShow(false);
      }}
      footer={null}
      width="70%"
    >
      <Spin spinning={isLoading}>
        <Row
          align="middle"
          justify="end"
          style={{ rowGap: "0.5em" }}
          className="review-detail-border-box"
        >
          <Col>
            <Button danger={currReview?.is_best} onClick={handleReviewBest}>
              {currReview.is_best ? "베스트 해제" : "베스트 선정"}
            </Button>
          </Col>
          <Col style={{ marginLeft: "1em" }} onClick={handleReviewShow}>
            <Button disabled={currReview?.review_private === 0}>노출</Button>
          </Col>
          <Col style={{ marginLeft: "1em" }} onClick={handleReviewNoShow}>
            <Button disabled={currReview?.review_private !== 0}>미노출</Button>
          </Col>
        </Row>
        <Row align="top" className="review-detail-review-box">
          {isPhotoReview && (
            <Col span={12} className="review-img-box">
              <Row
                justify="center"
                style={{
                  marginBottom: "1em",
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                }}
              >
                {isLoading ? (
                  <Skeleton.Image className={`main`} />
                ) : (
                  <Image
                    className="review-main-img"
                    alt={`review main img`}
                    src={`${process.env.REACT_APP_S3_HOST}/image/review/${
                      currReview?.product_id
                    }/${currReview?.created_by}_${
                      currReview?.id
                    }_${currentPhotoIdx}.${
                      currReview?.review_title.split("-")[1]
                    }`}
                  />
                )}
              </Row>
              <Row
                gutter={8}
                style={{ rowGap: "0.5em" }}
                className={`review-img-list`}
              >
                {renderReviewImageList()?.map((item) => (
                  <Col
                    key={item.idx}
                    onClick={() => handleReviewImageListClick(item.idx)}
                  >
                    <div
                      className={`review-sub-img-box`}
                      style={{
                        border:
                          item.idx === currentPhotoIdx
                            ? "3px solid #117ffa"
                            : "3px solid #868e96",
                      }}
                    >
                      {isLoading ? (
                        <Skeleton.Image className={`sub`} />
                      ) : (
                        <img
                          className="review-sub-img"
                          src={item.source}
                          alt={`review-img-${item.idx}`}
                        />
                      )}
                      <div className={`review-sub-img-hover`}></div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          )}
          <Col span={isPhotoReview ? 12 : 24} className="review-content-box">
            <Row align="middle">
              {isLoading ? (
                <Skeleton paragraph={{ rows: 1 }} />
              ) : (
                <>
                  <Row
                    align="middle"
                    justify="space-between"
                    style={{ width: "100%", paddingBottom: "0.5em" }}
                  >
                    <Col style={{ paddingRight: "0.5em" }}>
                      {renderReviewStar(currReview?.review_score)}
                    </Col>
                    <Row
                      align="middle"
                      className={`review-create-info`}
                      style={{ color: "#868e96" }}
                    >
                      <Col>{currReview?.created_by_name}</Col>
                      <Col>
                        <Divider
                          type="vertical"
                          style={{ margin: "0 0.5em" }}
                        />
                      </Col>
                      <Col>{currReview?.created_at.split("T")[0]}</Col>
                    </Row>
                  </Row>
                  <Row align="middle">{currReview?.product_name}</Row>
                </>
              )}
            </Row>
            <Divider style={{ margin: "1em 0" }} />
            <Row align="middle">
              {isLoading ? (
                <Skeleton title={false} paragraph={{ rows: 6 }} />
              ) : (
                <Col
                  style={{
                    paddingBottom: "1em",
                    wordBreak: "break-word",
                  }}
                >
                  {currReview?.review_body}
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <Row
          align="middle"
          justify="space-between"
          className="review-detail-border-box"
        >
          <Col>
            <Button onClick={handlePrevReviewClick}>{`< 이전 리뷰`}</Button>
          </Col>
          <Col>
            <Button onClick={handleNextReviewClick}>{`다음 리뷰 >`}</Button>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};

export default ReviewDetailModal;
